import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SettingDto } from '@tww/shared';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorState, Skeleton } from '../../ui/basics';
import { Checkbox, Field, NumberInput, Select, TextArea, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { useUnsaved } from './adminUtil';
import { DEFS, GROUPS, settingLabel, type Def } from './settingDefs';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function SettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { refresh } = useAuth();
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api.get<{ items: SettingDto[] }>('/settings').then((r) => r.items) });
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [resetN, setResetN] = useState(0);
  const items = q.data ?? [];
  const changed = items.filter((s) => s.key in draft && !same(draft[s.key], s.value));
  const dirty = changed.length > 0;
  useUnsaved(dirty);
  const save = useMutation({
    mutationFn: async () => {
      for (const s of changed) await api.put(`/settings/${s.key}`, { value: draft[s.key] });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings'] });
      await refresh();
      setDraft({});
      toast(changed.length === 1 ? 'Einstellung gespeichert' : `${changed.length} Einstellungen gespeichert`);
    },
    onError: (e: Error) => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast(e.message, 'error');
    },
  });
  if (q.isLoading) return <Skeleton rows={8} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const val = (s: SettingDto) => (s.key in draft ? draft[s.key] : s.value);
  const set = (key: string, v: unknown) => setDraft((d) => ({ ...d, [key]: v }));
  const setErr = (key: string, e: string | null) =>
    setInvalid((m) => {
      const n = { ...m };
      if (e) n[key] = e;
      else delete n[key];
      return n;
    });
  const known = items.filter((s) => DEFS[s.key] && DEFS[s.key]!.group !== 'Erweitert');
  const advanced = items.filter((s) => DEFS[s.key]?.group === 'Erweitert');
  const unknown = items.filter((s) => !DEFS[s.key]);
  return (
    <div className="settings" key={resetN}>
      {GROUPS.map((g) => {
        const list = known.filter((s) => DEFS[s.key]!.group === g);
        if (!list.length) return null;
        return (
          <section key={g} className="section">
            <div className="sh">
              <h2>{g}</h2>
            </div>
            <div className="sb">
              <div className="form">
                {list.map((s) => (
                  <SettingField key={s.key} def={DEFS[s.key]!} value={val(s)} onChange={(v) => set(s.key, v)} error={invalid[s.key]} onError={(e) => setErr(s.key, e)} />
                ))}
              </div>
            </div>
          </section>
        );
      })}
      {unknown.length + advanced.length > 0 && (
        <details className="fs section">
          <summary>Erweitert</summary>
          <div className="body">
            <div className="form one">
              {advanced.map((s) => (
                <SettingField key={s.key} def={DEFS[s.key]!} value={val(s)} onChange={(v) => set(s.key, v)} error={invalid[s.key]} onError={(e) => setErr(s.key, e)} />
              ))}
              {unknown.map((s) => (
                <RawField key={s.key} setting={s} value={val(s)} onChange={(v) => set(s.key, v)} />
              ))}
            </div>
          </div>
        </details>
      )}
      <div className="save-bar">
        <span className="mut small">{dirty ? (changed.length === 1 ? '1 Änderung nicht gespeichert' : `${changed.length} Änderungen nicht gespeichert`) : 'Alle Änderungen gespeichert'}</span>
        <span className="btn-row">
          <button type="button" className="btn" disabled={!dirty || save.isPending} onClick={() => (setDraft({}), setInvalid({}), setResetN((n) => n + 1))}>
            Verwerfen
          </button>
          <button type="button" className="btn primary" disabled={!dirty || save.isPending || Object.keys(invalid).length > 0} onClick={() => save.mutate()}>
            {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </span>
      </div>
    </div>
  );
}

function SettingField({ def, value, onChange, error, onError }: { def: Def; value: unknown; onChange: (v: unknown) => void; error?: string; onError: (e: string | null) => void }) {
  const label = def.unit ? `${def.label} (${def.unit})` : def.label;
  switch (def.type) {
    case 'number':
      return <NumberInput label={label} value={typeof value === 'number' ? value : null} onChange={(v) => (v == null ? onError('Bitte eine Zahl eingeben') : (onError(null), onChange(v)))} hint={def.hint} error={error} />;
    case 'bool':
      return (
        <div className="field full">
          <Checkbox label={def.label} value={Boolean(value)} onChange={onChange} hint={def.hint} />
        </div>
      );
    case 'text':
      return <TextInput label={label} value={value == null ? '' : String(value)} onChange={onChange} hint={def.hint} type={def.inputType} placeholder={def.placeholder} full={String(value ?? '').length > 40 || def.label.startsWith('Muster')} />;
    case 'numberList':
      return <NumberListInput label={label} value={Array.isArray(value) ? (value as number[]) : []} onChange={onChange} hint={def.hint} error={error} onError={onError} />;
    case 'monthRange': {
      const w = (value ?? {}) as { from_month?: number; to_month?: number };
      const opts = MONTHS.map((m, i) => ({ value: String(i + 1), label: m }));
      const bad = w.from_month != null && w.to_month != null && w.from_month > w.to_month;
      return (
        <Field label={label} hint={def.hint} error={bad ? 'Der Beginn liegt nach dem Ende' : undefined}>
          <div className="form sub-form">
            <Select label="von" value={String(w.from_month ?? '')} onChange={(v) => onChange({ ...w, from_month: Number(v) || 1 })} options={opts} />
            <Select label="bis" value={String(w.to_month ?? '')} onChange={(v) => onChange({ ...w, to_month: Number(v) || 12 })} options={opts} />
          </div>
        </Field>
      );
    }
    case 'object': {
      const o = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
      return (
        <Field label={label} hint={def.hint} full>
          <div className="form sub-form">
            {def.fields.map((sf) =>
              sf.type === 'number' ? (
                <NumberInput key={sf.key} label={sf.label} value={typeof o[sf.key] === 'number' ? (o[sf.key] as number) : null} onChange={(v) => onChange({ ...o, [sf.key]: v ?? 0 })} />
              ) : (
                <TextInput key={sf.key} label={sf.label} value={o[sf.key] == null ? '' : String(o[sf.key])} onChange={(v) => onChange({ ...o, [sf.key]: v })} />
              ),
            )}
          </div>
        </Field>
      );
    }
  }
}

/** Zahlenliste wie „2, 5“ */
function NumberListInput({ label, value, onChange, hint, error, onError }: { label: string; value: number[]; onChange: (v: number[]) => void; hint?: string; error?: string; onError: (e: string | null) => void }) {
  const [text, setText] = useState(value.join(', '));
  const [prev, setPrev] = useState(value);
  if (!same(prev, value)) {
    setPrev(value);
    setText(value.join(', '));
  }
  return (
    <TextInput
      label={label}
      value={text}
      hint={hint}
      error={error}
      onChange={(t) => {
        setText(t);
        const parts = t.split(/[,;\s]+/).filter(Boolean);
        const nums = parts.map((p) => Number(p.replace(',', '.')));
        if (nums.some((n) => !Number.isFinite(n) || n <= 0)) return onError('Bitte positive Zahlen durch Komma getrennt eingeben');
        onError(null);
        const sorted = [...new Set(nums)].sort((a, b) => a - b);
        setPrev(sorted);
        onChange(sorted);
      }}
    />
  );
}

/** Unbekannte Einstellung: Text, bei strukturierten Werten als Rohtext */
function RawField({ setting: s, value, onChange }: { setting: SettingDto; value: unknown; onChange: (v: unknown) => void }) {
  const structured = typeof s.value !== 'string';
  const [text, setText] = useState(structured ? JSON.stringify(value) : String(value ?? ''));
  const label = settingLabel(s.key);
  return (
    <TextArea
      label={label}
      value={text}
      rows={text.length > 60 ? 3 : 1}
      hint={s.description ?? undefined}
      onChange={(t) => {
        setText(t);
        if (!structured) return onChange(t);
        try {
          onChange(JSON.parse(t));
        } catch {
          onChange(t);
        }
      }}
    />
  );
}
